/**
 * Drives the per-component size pickers on a bundle PDP.
 *
 * Each component owns one of the bundle product's options, so a complete set of
 * choices identifies exactly one bundle variant.
 *
 * WHY THIS DRIVES THE STOCK PICKER RATHER THAN THE CART FORM
 *
 * The first version wrote the resolved variant id straight into the add-to-cart
 * form's hidden input. That added to cart correctly but left the rest of the page
 * stale: the inventory counter, price and buy buttons still described the variant
 * the page was rendered with, because selecting a variant in this theme is not a
 * matter of setting that input. variant-picker.js dispatches a ProductSelectEvent
 * and re-fetches the section so the server re-renders those blocks - which is why
 * "4 left" never changed no matter which sizes were picked.
 *
 * So this no longer bypasses the stock picker; it operates it. The picker is still
 * in the DOM (hidden by CSS - see the block), and ticking its radios makes the
 * theme do everything it normally does. Nothing here re-implements variant
 * resolution, inventory or the cart.
 *
 * Availability is per combination, not per size: a size is disabled only when no
 * purchasable variant contains it given what is selected elsewhere, so the grid is
 * recomputed on every change.
 */
class BundleComponents extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('script[data-bundle-variants]');
    if (!json) return;

    try {
      this.variants = JSON.parse(json.textContent);
    } catch {
      // A malformed payload must not take the page down. The stock picker is
      // still present and functional underneath; revealing it is a better
      // failure than a dead set of pills.
      this.#revealFallback();
      return;
    }

    this.inputs = [...this.querySelectorAll('input[type="radio"][data-option-index]')];
    if (!this.inputs.length) return;

    this.addEventListener('change', this.#onChange);
    this.#sync({ propagate: false });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  #onChange = (event) => {
    if (event.target instanceof HTMLInputElement) this.#sync({ propagate: true });
  };

  /** If this component cannot work, let the stock picker show rather than nothing. */
  #revealFallback() {
    this.remove();
  }

  /** Currently selected value per option index, or null where nothing is chosen. */
  #selection() {
    const out = [];
    for (const input of this.inputs) {
      const i = Number(input.dataset.optionIndex);
      if (input.checked) out[i] = input.value;
      else if (out[i] === undefined) out[i] = null;
    }
    return out;
  }

  #sync({ propagate }) {
    const selected = this.#selection();

    // Disable any size that cannot be completed into a purchasable combination,
    // holding the other components' current choices fixed.
    for (const input of this.inputs) {
      const i = Number(input.dataset.optionIndex);
      const candidate = selected.slice();
      candidate[i] = input.value;
      const reachable = this.variants.some(
        (v) => v.available && candidate.every((val, idx) => val == null || v.options[idx] === val)
      );
      input.disabled = !reachable;
      if (input.disabled && input.checked) input.checked = false;
    }

    if (!propagate || selected.some((v) => v == null)) return;
    this.#selectInStockPicker(selected);
  }

  /**
   * Mirror the selection onto the theme's own variant picker and let it take over.
   * @param {(string|null)[]} selected
   */
  #selectInStockPicker(selected) {
    // Re-queried every time: variant-picker.js morphs its own markup after each
    // change, so any references cached earlier would be stale.
    const picker = document.querySelector('variant-picker');
    if (!picker) return;

    let changed = null;
    selected.forEach((value, index) => {
      if (value == null) return;
      const input = picker.querySelector(
        `input[type="radio"][data-fieldset-index="${index}"][value="${CSS.escape(value)}"]`
      );
      if (input instanceof HTMLInputElement && !input.checked) {
        input.checked = true;
        changed = input;
      }
    });

    // One event after all options are set, not one per option: each change
    // triggers a section re-fetch, and firing several would race them.
    changed?.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('bundle-components')) {
  customElements.define('bundle-components', BundleComponents);
}
